import React, { useState, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  TextField,
  InputAdornment,
  Checkbox,
  Chip,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Skeleton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Helper to get effective value (edited or original)
const getEffectiveValue = (obj, field) => {
  const editedField = `${field}_edited`;
  return obj[editedField] && obj[editedField] !== '' ? obj[editedField] : obj[field];
};

// Format date for display
const formatDate = (date) => {
  if (!date) return 'No date';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid date';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0.00';
  const num = parseFloat(value);
  if (isNaN(num)) return '$0.00';
  return `$${num.toFixed(2)}`;
};

function ReceiptSelector({ 
  receipts, 
  selectedReceipts, 
  onSelectionChange, 
  isLoading = false 
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [imageLoadingStates, setImageLoadingStates] = useState({});

  // Filter and sort receipts
  const filteredReceipts = useMemo(() => {
    let filtered = receipts;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(receipt => {
        const merchant = (getEffectiveValue(receipt, 'merchant_name') || '').toLowerCase();
        const date = formatDate(getEffectiveValue(receipt, 'date')).toLowerCase();
        return merchant.includes(term) || date.includes(term);
      });
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(getEffectiveValue(b, 'date') || 0) - new Date(getEffectiveValue(a, 'date') || 0);
        case 'date_asc':
          return new Date(getEffectiveValue(a, 'date') || 0) - new Date(getEffectiveValue(b, 'date') || 0);
        case 'merchant':
          return (getEffectiveValue(a, 'merchant_name') || '').localeCompare(getEffectiveValue(b, 'merchant_name') || '');
        case 'amount_desc':
          return parseFloat(getEffectiveValue(b, 'total_cost') || 0) - parseFloat(getEffectiveValue(a, 'total_cost') || 0);
        case 'amount_asc':
          return parseFloat(getEffectiveValue(a, 'total_cost') || 0) - parseFloat(getEffectiveValue(b, 'total_cost') || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [receipts, searchTerm, sortBy]);

  // Calculate selection summary
  const selectionSummary = useMemo(() => {
    const selectedList = receipts.filter(r => selectedReceipts.includes(r.uuid));
    const total = selectedList.reduce((sum, r) => {
      const cost = parseFloat(getEffectiveValue(r, 'total_cost') || 0);
      return sum + (isNaN(cost) ? 0 : cost);
    }, 0);
    
    const dates = selectedList
      .map(r => new Date(getEffectiveValue(r, 'date')))
      .filter(d => !isNaN(d.getTime()))
      .sort((a, b) => a - b);
    
    return {
      count: selectedList.length,
      total,
      minDate: dates[0],
      maxDate: dates[dates.length - 1]
    };
  }, [receipts, selectedReceipts]);

  const handleToggleReceipt = (uuid) => {
    if (selectedReceipts.includes(uuid)) {
      onSelectionChange(selectedReceipts.filter(id => id !== uuid));
    } else {
      onSelectionChange([...selectedReceipts, uuid]);
    }
  };

  const handleSelectAll = () => {
    const allIds = filteredReceipts.map(r => r.uuid);
    const allSelected = allIds.every(id => selectedReceipts.includes(id));
    
    if (allSelected) {
      // Deselect all filtered
      onSelectionChange(selectedReceipts.filter(id => !allIds.includes(id)));
    } else {
      // Select all filtered
      const newSelection = [...new Set([...selectedReceipts, ...allIds])];
      onSelectionChange(newSelection);
    }
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  const handleImageLoad = (uuid) => {
    setImageLoadingStates(prev => ({ ...prev, [uuid]: false }));
  };

  const handleImageError = (uuid) => {
    setImageLoadingStates(prev => ({ ...prev, [uuid]: 'error' }));
  };

  const allFilteredSelected = filteredReceipts.length > 0 && 
    filteredReceipts.every(r => selectedReceipts.includes(r.uuid));

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Skeleton variant="rounded" width="60%" height={40} />
          <Skeleton variant="rounded" width="40%" height={40} />
        </Box>
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} variant="rounded" height={80} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Search and filter bar */}
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <TextField
          placeholder="Search merchants..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={sortBy}
            label="Sort by"
            onChange={(e) => setSortBy(e.target.value)}
          >
            <MenuItem value="date_desc">Date (Newest)</MenuItem>
            <MenuItem value="date_asc">Date (Oldest)</MenuItem>
            <MenuItem value="merchant">Merchant</MenuItem>
            <MenuItem value="amount_desc">Amount (High)</MenuItem>
            <MenuItem value="amount_asc">Amount (Low)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Batch selection controls */}
      <Box sx={{ 
        px: 2, 
        py: 1.5, 
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.02)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            size="small"
            variant={allFilteredSelected ? "contained" : "outlined"}
            onClick={handleSelectAll}
            sx={{ 
              textTransform: 'none', 
              fontSize: '0.8rem',
              ...(allFilteredSelected && {
                backgroundColor: '#22c55e',
                '&:hover': { backgroundColor: '#16a34a' }
              })
            }}
          >
            {allFilteredSelected ? '✓ All Selected' : `Select All (${filteredReceipts.length})`}
          </Button>
          {selectedReceipts.length > 0 && (
            <Button
              size="small"
              variant="text"
              onClick={handleClearAll}
              sx={{ textTransform: 'none', fontSize: '0.8rem', color: 'text.secondary' }}
            >
              Clear Selection
            </Button>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Showing {filteredReceipts.length} of {receipts.length} receipts
        </Typography>
      </Box>

      {/* Receipt list */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        minHeight: 300,
        maxHeight: 400
      }}>
        {filteredReceipts.length === 0 ? (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center', 
            color: 'text.secondary' 
          }}>
            <Typography variant="body2">
              {searchTerm ? 'No receipts match your search' : 'No receipts available'}
            </Typography>
          </Box>
        ) : (
          filteredReceipts.map((receipt) => {
            const isSelected = selectedReceipts.includes(receipt.uuid);
            const isPDF = receipt.mime_type?.startsWith('application/pdf');
            const imageUrl = receipt.receipt_image_uri;
            const isImageLoading = imageLoadingStates[receipt.uuid] === undefined || imageLoadingStates[receipt.uuid] === true;
            const imageError = imageLoadingStates[receipt.uuid] === 'error';

            return (
              <Box
                key={receipt.uuid}
                onClick={() => handleToggleReceipt(receipt.uuid)}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: isSelected 
                      ? 'rgba(59, 130, 246, 0.15)' 
                      : 'rgba(255, 255, 255, 0.03)'
                  }
                }}
              >
                {/* Checkbox */}
                <Checkbox
                  checked={isSelected}
                  sx={{
                    p: 0.5,
                    color: 'rgba(255, 255, 255, 0.3)',
                    '&.Mui-checked': {
                      color: '#3b82f6',
                    }
                  }}
                />

                {/* Thumbnail */}
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 1,
                    overflow: 'hidden',
                    flexShrink: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: isSelected 
                      ? '2px solid #3b82f6' 
                      : '1px solid rgba(255, 255, 255, 0.1)',
                    position: 'relative'
                  }}
                >
                  {isPDF ? (
                    <PictureAsPdfIcon sx={{ color: '#ef4444', fontSize: 28 }} />
                  ) : imageUrl && !imageError ? (
                    <>
                      {isImageLoading && (
                        <CircularProgress size={20} sx={{ position: 'absolute' }} />
                      )}
                      <img
                        src={imageUrl}
                        alt={getEffectiveValue(receipt, 'merchant_name')}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: isImageLoading ? 'none' : 'block'
                        }}
                        onLoad={() => handleImageLoad(receipt.uuid)}
                        onError={() => handleImageError(receipt.uuid)}
                      />
                    </>
                  ) : (
                    <ImageIcon sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: 24 }} />
                  )}
                  
                  {/* Selection indicator overlay */}
                  {isSelected && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        backgroundColor: '#3b82f6',
                        borderRadius: '50%',
                        width: 18,
                        height: 18,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 14, color: 'white' }} />
                    </Box>
                  )}
                </Box>

                {/* Receipt info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500, 
                      mb: 0.25,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {getEffectiveValue(receipt, 'merchant_name') || 'Unknown Merchant'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(getEffectiveValue(receipt, 'date'))}
                  </Typography>
                </Box>

                {/* Amount */}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600, 
                    color: isSelected ? '#60a5fa' : 'text.primary',
                    fontFamily: 'monospace'
                  }}
                >
                  {formatCurrency(getEffectiveValue(receipt, 'total_cost'))}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>

      {/* Selection summary */}
      {selectedReceipts.length > 0 && (
        <Box sx={{ 
          p: 2, 
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(34, 197, 94, 0.05) 100%)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#60a5fa' }}>
              📊 Selection Summary
            </Typography>
            <Chip
              label={`${selectionSummary.count} receipt${selectionSummary.count !== 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Total Amount</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                {formatCurrency(selectionSummary.total)}
              </Typography>
            </Box>
            {selectionSummary.minDate && (
              <Box>
                <Typography variant="caption" color="text.secondary">Date Range</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatDate(selectionSummary.minDate)}
                  {selectionSummary.maxDate && selectionSummary.minDate !== selectionSummary.maxDate && (
                    <> — {formatDate(selectionSummary.maxDate)}</>
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default ReceiptSelector;

